import { useEffect, useRef, useState } from 'react'
import { useDebounce } from '@uidotdev/usehooks'
import fetcher from '../fetcher'

const VIN_LENGTH = 17
const DEBOUNCE_MS = 400

const IDLE = { result: null, loading: false, error: null }

const normalize = (raw) => String(raw || '').trim().toUpperCase()

// Decode a VIN into a plain vehicle, over the same endpoint the calculator uses
// (`GET /cars/<vin>/?is_used=`). Only the identity of the car is taken — none of
// Calculate's MSRP / cost / base_car_id prefill, which is why this is a hook
// over the fetcher rather than a reuse of CalcContext.
//
// The endpoint is a billed vendor call, so a VIN is decoded once: the value is
// debounced and the last decoded VIN is remembered.
export const useVinDecode = (vin, { isUsed = true } = {}) => {
    const [state, setState] = useState(IDLE)
    const debouncedVin = useDebounce(vin, DEBOUNCE_MS)
    // Guards out-of-order responses: a slow decode must never overwrite the
    // result of a VIN typed after it.
    const seq = useRef(0)
    const decodedRef = useRef(null)

    useEffect(() => {
        const clean = normalize(debouncedVin)

        if (clean.length !== VIN_LENGTH) {
            // A partial VIN is not a failure — drop any stale error so the
            // manual fields aren't left with a warning under them. Returning
            // the same object keeps typing from re-rendering.
            decodedRef.current = null
            setState((prev) => (prev === IDLE ? prev : IDLE))
            return
        }
        if (decodedRef.current === clean) return

        decodedRef.current = clean
        const mySeq = ++seq.current
        setState({ result: null, loading: true, error: null })

        fetcher.getCarByVin(clean, isUsed)
            .then((res) => {
                if (mySeq !== seq.current) return
                const data = res?.data
                if (!data) { setState({ result: null, loading: false, error: 'empty' }); return }
                setState({
                    result: {
                        vin: clean,
                        year: data.year != null ? String(data.year) : '',
                        make: data.make?.cleaned_name || data.make?.name || '',
                        model: data.model?.cleaned_name || data.model?.name || '',
                        // Empty when the VIN matched more than one trim — the
                        // decode is genuinely unresolved and the user picks.
                        trim: data.trim?.name || '',
                        // The decode already carries the option lists, so the
                        // cascade doesn't have to re-fetch what we just got.
                        makes: data.makes || [],
                        models: data.models || [],
                        trims: data.trims || [],
                        candidates: data.vin_vehicles || [],
                    },
                    loading: false,
                    error: null,
                })
            })
            .catch((error) => {
                if (mySeq !== seq.current) return
                // An unmatched VIN comes back as a server error; it is a normal
                // outcome here, not a crash.
                setState({ result: null, loading: false, error })
            })
    }, [debouncedVin, isUsed])

    return state
}
